import { z } from "zod";
import { gstStateName, isGstStateCode } from "./state-codes.js";
/**
 * GSTIN format, verbatim from spec §4.
 *
 * `27ABCDE1234F1Z5` decomposes as:
 *   27     state code (must exist in GST_STATE_CODES)
 *   ABCDE  first five PAN characters
 *   1234   PAN numeric block
 *   F      PAN holder-name letter
 *   1      entity / registration number for that PAN in the state
 *   Z      fixed literal
 *   5      checksum character
 *
 * No `g` flag — this RegExp is shared and reused, and a sticky/global flag would
 * carry `lastIndex` between `.test()` calls.
 */
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
/** Length of a GSTIN. */
export const GSTIN_LENGTH = 15;
/**
 * A GSTIN string: correct shape AND a state code that actually exists (spec §4 —
 * both halves of the rule are mandatory, the regex alone accepts `99...`).
 * Case is NOT normalised: a GSTIN is uppercase by definition, so lowercase input
 * is rejected rather than silently "fixed".
 */
export const GstinSchema = z
    .string()
    .trim()
    .length(GSTIN_LENGTH, `GSTIN must be exactly ${GSTIN_LENGTH} characters`)
    .regex(GSTIN_REGEX, "GSTIN format is invalid (expected e.g. 27ABCDE1234F1Z5)")
    .refine((value) => isGstStateCode(value.slice(0, 2)), {
    message: "The first two digits of the GSTIN are not a valid Indian GST state code",
});
/**
 * State code embedded in a GSTIN, or `undefined` if the GSTIN is malformed or
 * carries an unknown state code. Spec §4: company.state_code is GSTIN[0:2], and
 * a registered customer's state code likewise.
 */
export function stateCodeFromGstin(gstin) {
    const trimmed = gstin.trim();
    if (!GSTIN_REGEX.test(trimmed))
        return undefined;
    const code = trimmed.slice(0, 2);
    return isGstStateCode(code) ? code : undefined;
}
/**
 * The PAN embedded in a GSTIN — characters 3 to 12 — or `null` if malformed.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A REGISTERED PARTY'S PAN IS NOT A SECOND FACT TO STORE. It is a slice of a
 * number the document already prints, which is why `customers.pan` is only ever
 * populated for a buyer with NO GSTIN (Rule 114B's unregistered case) and the
 * write path refuses both at once.
 *
 * ONE IMPLEMENTATION, BECAUSE THERE WERE THREE. This rule lived in
 * `apps/api/src/services/pdf/invoice-pdf.ts`, again inline in the SPA's customer
 * editor as `gstinClean.slice(2, 12)`, and was about to be written a fourth time
 * for the invoice detail screen. Three copies of a slice that must agree with
 * what is printed on a tax invoice is three chances to disagree — and the one
 * that drifts is the one nobody is looking at.
 *
 * It lives HERE rather than in `packages/tax` because it is not arithmetic: it
 * is a fact about the GSTIN format, next to the regex that defines it.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function panFromGstin(gstin) {
    if (typeof gstin !== "string")
        return null;
    // Uppercased and stripped of separators first: a GSTIN pasted out of a PDF or
    // an e-way bill arrives with internal spaces, and the strict regex would
    // reject a value the reader can see is correct.
    const cleaned = gstin.toUpperCase().replace(/[^0-9A-Z]/g, "");
    if (!GSTIN_REGEX.test(cleaned))
        return null;
    return cleaned.slice(2, 12);
}
/** Validates a GSTIN and resolves its state, without throwing. */
export function validateGstin(gstin) {
    const stateCode = stateCodeFromGstin(gstin);
    if (stateCode === undefined)
        return { valid: false };
    return { valid: true, stateCode, stateName: gstStateName(stateCode) };
}
//# sourceMappingURL=gstin.js.map